import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Box, Typography, FormGroup, Grid } from "@mui/material";
import EMPCheckbox from "../EMPCheckbox/EMPCheckbox";
import EMPButton from "../EMPButton/EMPButton";
import SharedToolTip from "../../sharedToolTip/SharedToolTip";
import { trackFilterToggle } from "../../services/analytics/OrdersPageAnalytics";
import { UserContext } from "../../context/userContext";
import HasPermissionTo from "../../../common/HasPermissionTo";

const FilterOption = ({
  category,
  option,
  checked,
  onFilterChange,
  disabled,
  isLincoln,
  user,
}) => {
  const handleChange = (isChecked) => {
    if (category === "actionRequired") {
      trackFilterToggle("show only action required", isChecked, user);
    }
    return onFilterChange(category, option.value, isChecked);
  };

  return (
    <Box
      sx={
        isLincoln
          ? { height: "30px", display: "flex", alignItems: "center" }
          : { mb: 3 }
      }
    >
      <EMPCheckbox
        checked={checked}
        handleChange={handleChange}
        id={`filter-${category}-${option.value}`}
        label={option.label}
        disabled={disabled}
      />
    </Box>
  );
};

FilterOption.propTypes = {
  category: PropTypes.string.isRequired,
  option: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.node,
  }).isRequired,
  checked: PropTypes.bool,
  onFilterChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isLincoln: PropTypes.bool,
  user: PropTypes.object,
};

FilterOption.defaultProps = {
  checked: false,
  disabled: false,
  isLincoln: false,
  user: undefined,
};

const lincolnGroupTitleSx = {
  fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
};

const FilterGroup = ({
  category,
  filterGroup,
  selectedValues,
  onFilterChange,
  disabled,
  isLincoln,
  user,
}) => (
  <>
    <Grid
      container
      alignItems="center"
      sx={{
        mb: isLincoln ? "10px" : 2,
        ...(isLincoln
          ? {
              lineHeight: "20px",
              "& > .MuiGrid-item": { display: "flex", alignItems: "center" },
            }
          : {}),
      }}
    >
      <Grid item sx={{ mr: isLincoln ? "8px" : 1 }}>
        <Typography variant="body2" sx={isLincoln ? lincolnGroupTitleSx : {}}>
          {filterGroup.title}
        </Typography>
      </Grid>
      {filterGroup.hasInfo && (
        <Grid
          item
          sx={{ display: "flex", alignItems: "center", height: "100%" }}
        >
          <SharedToolTip
            placement="right"
            title={filterGroup.infoTitle || filterGroup.title}
            body={filterGroup.infoText}
            showCloseIcon={isLincoln}
            useOutlinedInfoIcon={isLincoln}
            ariaLabel={`${filterGroup.title} information`}
            iconColor={isLincoln ? undefined : "#066fef"}
          />
        </Grid>
      )}
    </Grid>

    <FormGroup sx={{ mb: isLincoln ? "14px" : 6, pl: isLincoln ? "10px" : 0 }}>
      {filterGroup.options.map((option) => (
        <FilterOption
          key={option.value}
          category={category}
          option={option}
          checked={Boolean(selectedValues?.includes(option.value))}
          onFilterChange={onFilterChange}
          disabled={disabled}
          isLincoln={isLincoln}
          user={user}
        />
      ))}
    </FormGroup>
  </>
);

FilterGroup.propTypes = {
  category: PropTypes.string.isRequired,
  filterGroup: PropTypes.shape({
    title: PropTypes.node,
    infoTitle: PropTypes.node,
    hasInfo: PropTypes.bool,
    infoText: PropTypes.node,
    options: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  selectedValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onFilterChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isLincoln: PropTypes.bool,
  user: PropTypes.object,
};

FilterGroup.defaultProps = {
  selectedValues: [],
  disabled: false,
  isLincoln: false,
  user: undefined,
};

const EMPFilterSidebar = ({
  filters,
  selectedFilters,
  onFilterChange,
  onFilterClear = () => {},
  disabled = false,
  clearLabel = "Clear Filters",
  hideInactiveClear = false,
  hideClear = false,
}) => {
  const { user } = useContext(UserContext);
  const isLincoln = user?.brand === "L";
  const hasSelectedFilters = Object.values(selectedFilters).some(
    (filterValues) => filterValues.length > 0
  );

  return (
    <Box
      sx={{
        bgcolor: isLincoln ? "transparent" : "background.paper",
        opacity: disabled ? 0.7 : 1,
        pl: isLincoln ? 0 : 1,
        ...(isLincoln
          ? {
              "& .MuiFormControlLabel-label": {
                fontSize: 14,
                lineHeight: "20px",
                color: "#444",
                letterSpacing: 0,
              },
              "& .MuiCheckbox-root": {
                width: 18,
                height: 18,
                flexBasis: "18px",
                padding: 0,
                border: "1px solid #999",
                borderRadius: "3px",
                "&.Mui-checked": {
                  backgroundColor: "#485b65",
                  borderColor: "#485b65",
                },
                "& svg": { width: "16px", height: "16px" },
              },
            }
          : {}),
      }}
    >
      {Object.entries(filters).map(([category, filterGroup]) => {
        const groupProps = {
          category,
          filterGroup,
          selectedValues: selectedFilters[category],
          onFilterChange,
          disabled,
          isLincoln,
          user,
        };

        return filterGroup.permission ? (
          <HasPermissionTo
            key={category}
            perform={[filterGroup.permission]}
            permissions={user?.permissions?.rolePermissions}
            render={() => <FilterGroup {...groupProps} />}
          />
        ) : (
          <FilterGroup key={category} {...groupProps} />
        );
      })}
      {!hideClear && (!hideInactiveClear || hasSelectedFilters) && (
        <Box>
          <EMPButton
            onClick={onFilterClear}
            variant="outlined"
            disabled={disabled || !hasSelectedFilters}
            testId="clear-filters-button"
            sx={
              isLincoln
                ? {
                    borderRadius: "4px",
                    borderColor: "#485B65",
                    color: "#485B65",
                    padding: "8px 24px",
                    fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
                    fontSize: 14,
                    lineHeight: "20px",
                    "&:hover": {
                      borderColor: "#3A4A53",
                      backgroundColor: "rgba(72, 91, 101, 0.08)",
                    },
                  }
                : undefined
            }
          >
            {clearLabel}
          </EMPButton>
        </Box>
      )}
    </Box>
  );
};

EMPFilterSidebar.propTypes = {
  filters: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.node,
      infoTitle: PropTypes.node,
      hasInfo: PropTypes.bool,
      infoText: PropTypes.node,
      permission: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          label: PropTypes.node,
        })
      ).isRequired,
    })
  ).isRequired,
  selectedFilters: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  ).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onFilterClear: PropTypes.func,
  disabled: PropTypes.bool,
  clearLabel: PropTypes.string,
  hideInactiveClear: PropTypes.bool,
  hideClear: PropTypes.bool,
};

export default EMPFilterSidebar;
